import { Plugin, PluginKey } from "@tiptap/pm/state";
import { DOMSerializer } from "@tiptap/pm/model";
import { CellSelection, TableMap } from "@tiptap/pm/tables";

const copyParserPlugin = ({ editor }) => {
    return new Plugin({
        key: new PluginKey("copyParserPlugin"),
        props: {
            handleDOMEvents: {
                async copy(view, event) {
                    event.preventDefault();

                    const { state } = view;
                    const { selection } = state;

                    // Check if this is a cell selection (multiple table cells)
                    if (selection instanceof CellSelection) {
                        return handleCellSelectionCopy(view, event, editor);
                    }

                    const { from, to } = selection;
                    const slice = state.doc.slice(from, to);

                    const fragment = DOMSerializer.fromSchema(state.schema).serializeFragment(
                        slice.content,
                    );

                    const div = document.createElement("div");
                    div.appendChild(fragment);

                    const removeCommentAttribute = (node) => {
                        if (node.hasAttribute("comment")) {
                            node.removeAttribute("comment");
                        }
                    };

                    div.querySelectorAll("*[comment]").forEach((node) => {
                        removeCommentAttribute(node);
                    });

                    div.querySelectorAll('span[data-type="dictionary"]').forEach((node) => {
                        const key = node.getAttribute("data-key");
                        const content = editor.storage.dictionary.store.use(key)?.text ?? key;

                        node.textContent = content;
                    });

                    div.querySelectorAll('span[data-type="abbreviation"]').forEach((node) => {
                        const key = node.getAttribute("data-key");
                        const content = editor.storage.abbreviation.store.use(key)?.name ?? key;

                        node.textContent = content;
                    });

                    div.querySelectorAll("span[data-cross-reference").forEach((node) => {
                        const crossReferencesMap =
                            editor.storage["cross-reference"].store.crossReferencesMap;
                        const key = node.getAttribute("id");
                        const crossReference = crossReferencesMap[key];
                        const content = crossReference
                            ? `${crossReference.ref}`
                            : `\u26A0 Missing Reference`;

                        node.textContent = content;
                    });

                    const clipboardData = event.clipboardData || window.clipboardData;

                    clipboardData.setData("text/html", div.innerHTML);
                    clipboardData.setData("text/plain", div.innerText);

                    return true;
                },
            },
        },
    });
};

/**
 * Handle copy operation for CellSelection (multiple table cells)
 * This creates a table fragment with metadata about the selection dimensions
 * to enable MS Word-like paste behavior
 */
function handleCellSelectionCopy(view, event, editor) {
    const { state } = view;
    const selection = state.selection;

    if (!(selection instanceof CellSelection)) {
        return false;
    }

    try {
        const $anchorCell = selection.$anchorCell;

        // Find the table by traversing up the document tree
        let table = null;
        let tableDepth = null;

        for (let depth = $anchorCell.depth; depth >= 0; depth--) {
            const node = $anchorCell.node(depth);
            if (node?.type?.spec?.tableRole === "table") {
                table = node;
                tableDepth = depth;
                break;
            }
        }

        if (!table || tableDepth === null) {
            // Fall back to default copy behavior
            return false;
        }

        const tableStart = $anchorCell.start(tableDepth);
        const map = TableMap.get(table);

        // Calculate the selection rectangle
        const anchorPos = $anchorCell.pos - tableStart;
        const headPos = selection.$headCell.pos - tableStart;
        const rect = map.rectBetween(anchorPos, headPos);

        const { left, right, top, bottom } = rect;
        const numRows = bottom - top;
        const numCols = right - left;

        // Build a new table structure with only the selected cells
        const rows = [];

        for (let row = top; row < bottom; row++) {
            const rowCells = [];

            for (let col = left; col < right; col++) {
                const cellPos = map.map[row * map.width + col];
                const cell = table.nodeAt(cellPos);

                if (cell) {
                    // Check if this is the anchor position of the cell (not a spanned position)
                    const cellRect = map.findCell(cellPos);

                    if (cellRect.top === row && cellRect.left === col) {
                        // Calculate adjusted colspan/rowspan for cells that might extend beyond selection
                        const adjustedColspan = Math.min(cell.attrs.colspan || 1, right - col);
                        const adjustedRowspan = Math.min(cell.attrs.rowspan || 1, bottom - row);

                        rowCells.push({
                            node: cell,
                            adjustedColspan,
                            adjustedRowspan,
                        });
                    }
                }
            }

            if (rowCells.length > 0) {
                rows.push(rowCells);
            }
        }

        // Serialize to HTML
        const serializer = DOMSerializer.fromSchema(state.schema);

        // Create a wrapper table element with metadata
        const tableEl = document.createElement("table");
        tableEl.setAttribute("data-cell-paste", "true");
        tableEl.setAttribute("data-paste-rows", String(numRows));
        tableEl.setAttribute("data-paste-cols", String(numCols));

        const tbody = document.createElement("tbody");
        tableEl.appendChild(tbody);

        for (const rowCells of rows) {
            const tr = document.createElement("tr");

            for (const { node, adjustedColspan, adjustedRowspan } of rowCells) {
                const cellEl = document.createElement(
                    node.type.name === "tableHeader" ? "th" : "td",
                );

                // Copy cell attributes
                if (adjustedColspan > 1) {
                    cellEl.setAttribute("colspan", String(adjustedColspan));
                }
                if (adjustedRowspan > 1) {
                    cellEl.setAttribute("rowspan", String(adjustedRowspan));
                }

                // Copy style attributes
                if (node.attrs.verticalAlign) {
                    cellEl.style.verticalAlign = node.attrs.verticalAlign;
                }

                // Serialize cell content
                const cellContent = serializer.serializeFragment(node.content);
                cellEl.appendChild(cellContent);

                tr.appendChild(cellEl);
            }

            tbody.appendChild(tr);
        }

        // Process special elements (remove comments, resolve dictionary/abbreviation/cross-reference)
        tableEl.querySelectorAll("*[comment]").forEach((node) => {
            node.removeAttribute("comment");
        });

        tableEl.querySelectorAll('span[data-type="dictionary"]').forEach((node) => {
            const key = node.getAttribute("data-key");
            const content = editor.storage.dictionary?.store?.use(key)?.text ?? key;
            node.textContent = content;
        });

        tableEl.querySelectorAll('span[data-type="abbreviation"]').forEach((node) => {
            const key = node.getAttribute("data-key");
            const content = editor.storage.abbreviation?.store?.use(key)?.name ?? key;
            node.textContent = content;
        });

        tableEl.querySelectorAll("span[data-cross-reference]").forEach((node) => {
            const crossReferencesMap = editor.storage["cross-reference"]?.store?.crossReferencesMap;
            const key = node.getAttribute("id");
            const crossReference = crossReferencesMap?.[key];
            const content = crossReference ? `${crossReference.ref}` : `\u26A0 Missing Reference`;
            node.textContent = content;
        });

        const clipboardData = event.clipboardData || window.clipboardData;

        // Create wrapper div to include the table
        const wrapper = document.createElement("div");
        wrapper.appendChild(tableEl);

        clipboardData.setData("text/html", wrapper.innerHTML);
        clipboardData.setData("text/plain", tableEl.innerText);

        return true;
    } catch (e) {
        console.error("Error in cell selection copy:", e);
        return false; // Fall back to default copy behavior
    }
}

export default copyParserPlugin;
